import { useEffect, useState } from "react";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";
import { ENDPOINTS } from "API/apiConstants";
import API from "utils/helpers/APIHelper";

// IMPORTANT: this is a copy of extractPropertyKey inside Review.tsx.
// If you ever change one, change the other, or the two components will
// look up different legacy reviews.
const extractPropertyKey = (title: string): string => {
  const stopWords =
    /\d|bedroom|bathroom|apartment|house|studio|room|ocean|view|floor|story|suite|flat|penthouse/i;
  const words = title.trim().split(/\s+/);
  const result: string[] = [];

  for (const word of words) {
    if (stopWords.test(word)) break;
    result.push(word);
  }

  // Return at least 2 words, max 3
  return result.slice(0, 3).join(" ") || title;
};

type LegacySummary = { avg: number | null; count: number };
const EMPTY: LegacySummary = { avg: null, count: 0 };

// One request per property title, shared by every component on the page
// (host card, rating badge, cards...). Failed requests are not cached.
const legacyCache = new Map<string, Promise<LegacySummary>>();

const fetchLegacySummary = (title: string): Promise<LegacySummary> => {
  const key = extractPropertyKey(title);
  const cached = legacyCache.get(key);
  if (cached) return cached;

  const request = API.get(ENDPOINTS.LEGACY_REVIEWS(key))
    .then((res: any): LegacySummary => {
      const data = res?.data;
      const list = data?.legacy_reviews || [];
      return { avg: data?.average_rating ?? null, count: list.length };
    })
    .catch((error: unknown) => {
      legacyCache.delete(key);
      throw error;
    });
  legacyCache.set(key, request);
  return request;
};

/**
 * Same math as Review.tsx: weighted average of the new platform rating and
 * the legacy rating. Returns null when there is no rating at all.
 */
export const combineRatings = (
  newAvg: number,
  newCount: number,
  legacyAvg: number | null,
  legacyCount: number
): number | null => {
  if (newCount > 0 && legacyCount > 0 && legacyAvg !== null) {
    return (newAvg * newCount + legacyAvg * legacyCount) / (newCount + legacyCount);
  }
  if (newCount > 0) return newAvg;
  if (legacyCount > 0) return legacyAvg;
  return null;
};

export type UnifiedReviewSummary = {
  rating: number | null; // combined average, or null when there are no reviews
  count: number; // new-platform reviews + legacy reviews
  loading: boolean; // true while the legacy lookup is still running
};

export type RatingInput = {
  title?: string | null; // used to look up the legacy reviews
  average?: number | null; // new-platform average rating
  count?: number | null; // new-platform review count
};

/**
 * Combined (new + legacy) rating and review count for a property.
 * Starts with the new-platform numbers right away, then updates once the
 * legacy reviews have loaded. Without a title it makes no request at all.
 */
export const useUnifiedReviewSummaryFor = (input: RatingInput): UnifiedReviewSummary => {
  const [legacy, setLegacy] = useState<LegacySummary>(EMPTY);
  const [loading, setLoading] = useState<boolean>(!!input.title);
  const title = input.title;

  useEffect(() => {
    // Clear the previous property's legacy rating straight away
    setLegacy(EMPTY);
    if (!title) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchLegacySummary(title)
      .then((summary) => {
        if (!cancelled) setLegacy(summary);
      })
      .catch(() => {
        // Legacy rating is optional: on error just show the new-platform rating
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [title]);

  const newCount = input.count ?? 0;
  return {
    rating: combineRatings(input.average ?? 0, newCount, legacy.avg, legacy.count),
    count: newCount + legacy.count,
    loading,
  };
};

/** Same thing, reading the numbers from full property data. */
export const useUnifiedReviewSummary = (
  propertyData: PropertyToEdit | null
): UnifiedReviewSummary =>
  useUnifiedReviewSummaryFor({
    title: propertyData?.title,
    average: propertyData?.average?.average_review,
    count: propertyData?.average?.count,
  });

/** Just the combined rating (used by HostInformation). */
export const useUnifiedRating = (propertyData: PropertyToEdit | null): number | null =>
  useUnifiedReviewSummary(propertyData).rating;